import { OBJECT_ACL, OBJECT_ACL_HEADER, MAX_ORIGINAL_BYTES } from "../../shared/keys.ts";
import { rundeUmriss, type Umriss } from "../../shared/umriss.ts";
import type { Anordnung } from "../../shared/anordnung.ts";
import { putzeBeschriftung } from "../../shared/personen.ts";
import { applyPatches, findLocationPatches, readerFromBlob } from "./gps.ts";
import { macheVorschau, VorschauFehler } from "./vorschau.ts";

/**
 * Der Weg eines Bildes vom Telefon in den Bucket — und zurück auf die Fläche.
 *
 * Die Reihenfolge ist tragend und steht nur hier:
 *
 *   1. **Vorschau erzeugen** (schlägt das fehl, ist nichts passiert, und die Kundschaft
 *      erfährt es, bevor sie wartet)
 *   2. **Ortsdaten aus dem Original entfernen** — vor jedem Netzzugriff
 *   3. anmelden, zwei presignte URLs holen
 *   4. **beide Fassungen direkt in den Bucket legen**, nicht über den Server (4.1)
 *   5. fertigmelden — erst danach gibt es eine Zeile
 *
 * Schritt 2 vor Schritt 3 ist kein Zufall: wäre es andersherum, hätte der Server einen
 * Namen für ein Bild vergeben, dessen Ortsdaten noch drin sind, und ein Abbruch
 * dazwischen hinterließe genau das im Speicher.
 */

export interface AngemeldetesBild {
  bildId: string;
  original: string;
  vorschau: string;
  gueltigBis: number;
}

export interface BildAusListe {
  bildId: string;
  typ: string;
  bytes: number;
  breite: number;
  hoehe: number;
  zuKlein: boolean;
  hochgeladen: number;
  /** Der Umriss aus Schritt 3, oder `null` für „noch nicht umfahren" — und nur das
   *  schickt ein Foto in die Warteschlange (`shared/umriss.ts`). */
  umriss: Umriss | null;
  /** Der Platz in der Reihe (Schritt 4). Der Server gibt ihn **lückenlos** heraus, auch
   *  wenn in der Spalte noch überall 0 steht — die Liste kommt bereits sortiert. */
  reihenfolge: number;
  /** Faktor auf die Höhe aus dem Umriss, Standard 1 (`shared/anordnung.ts`). */
  groesse: number;
  /** Senkrechter Versatz in Prozent der Figurenhöhe, Standard 0. */
  versatz: number;
  /** Wie viele Personen dieses eine Bildelement zeigt — Pflichtfeld, Standard 1
   *  (`shared/personen.ts`). Die Zahl, die in V1 den Preis trägt. */
  personen: number;
  /** Freier Text, optional. Die sichtbare Hälfte der Zuordnung Bild ↔ Person. */
  beschriftung: string;
  original: string;
  vorschau: string;
}

/**
 * Ein Fehler, dessen Text **vor die Kundin darf**.
 *
 * Alles andere — ein abgerissenes Netz, ein 500er, ein kaputtes JSON — wird beim
 * Aufrufer zu einem allgemeinen deutschen Satz. Ohne diese Unterscheidung stünde
 * irgendwann ein `TypeError: Failed to fetch` auf einem Telefon (`CLAUDE.md`).
 */
export class MeldbarerFehler extends Error {
  /**
   * Ist die **Sitzung** weg — oder ist nur diese eine Anfrage schiefgegangen?
   *
   * ⚠️ Der Unterschied ist nicht kosmetisch, er entscheidet über die angebotene Rettung.
   * Bei einem verlorenen Zugang hilft nur ein neues Projekt; bei einem abgerissenen Netz
   * wäre genau das der Verlust der eigenen Arbeit — die Fotos liegen dann im Speicher,
   * aber ohne die gemerkte Projekt-ID kommt niemand mehr an sie heran.
   *
   * Gesetzt wird es allein am Statuscode 401, nicht am Text: der Text ist für Menschen.
   */
  readonly sitzungWeg: boolean;

  constructor(message: string, sitzungWeg = false) {
    super(message);
    this.name = "MeldbarerFehler";
    this.sitzungWeg = sitzungWeg;
  }
}

const ALLGEMEIN = "Da ist etwas schiefgegangen. Bitte versuch es gleich noch einmal.";
const OHNE_NETZ = "Keine Verbindung. Bitte versuch es noch einmal, wenn du wieder Empfang hast.";

async function json(antwort: Response): Promise<unknown> {
  try {
    return (await antwort.json()) as unknown;
  } catch {
    return null;
  }
}

/** Die Fehlermeldung des Servers — er formuliert sie deutsch, wir reichen sie durch. */
function fehlertext(rumpf: unknown): string {
  if (typeof rumpf === "object" && rumpf !== null) {
    const { fehler } = rumpf as { fehler?: unknown };
    if (typeof fehler === "string" && fehler.length > 0) return fehler;
  }
  return ALLGEMEIN;
}

async function anFrage(pfad: string, init?: RequestInit): Promise<unknown> {
  let antwort: Response;
  try {
    antwort = await fetch(pfad, {
      ...init,
      headers: { ...(init?.body ? { "Content-Type": "application/json" } : {}), ...init?.headers },
    });
  } catch {
    throw new MeldbarerFehler(OHNE_NETZ);
  }
  const rumpf = await json(antwort);
  if (!antwort.ok) throw new MeldbarerFehler(fehlertext(rumpf), antwort.status === 401);
  return rumpf;
}

/** Ein neues Projekt. Der Server setzt dabei das Cookie; die ID merkt sich der Browser. */
export async function projektAnlegen(): Promise<string> {
  const rumpf = await anFrage("/api/projekte", { method: "POST" });
  const { id } = rumpf as { id?: unknown };
  if (typeof id !== "string") throw new MeldbarerFehler(ALLGEMEIN);
  return id;
}

async function bildAnmelden(
  projektId: string,
  typ: string,
  groesse: number,
): Promise<AngemeldetesBild> {
  const rumpf = await anFrage(`/api/projekte/${encodeURIComponent(projektId)}/bilder`, {
    method: "POST",
    body: JSON.stringify({ typ, groesse }),
  });
  return rumpf as AngemeldetesBild;
}

/**
 * Eine Fassung in den Bucket legen.
 *
 * ⚠️ Die beiden Kopfzeilen sind **nicht** schmückend: der Server hat sie mitsigniert
 * (`server/storage.ts`, `presignPut`). Fehlt eine oder trägt sie einen anderen Wert,
 * bricht die Signatur und der Speicher antwortet 403 — auf **jeden** Upload, nicht nur
 * auf einen verdächtigen. Der Wert kommt deshalb aus derselben Konstante wie beim
 * Signieren (`shared/keys.ts`, `OBJECT_ACL`).
 */
async function inDenSpeicher(url: string, koerper: Blob, typ: string): Promise<void> {
  let antwort: Response;
  try {
    antwort = await fetch(url, {
      method: "PUT",
      body: koerper,
      headers: { "Content-Type": typ, [OBJECT_ACL_HEADER]: OBJECT_ACL },
    });
  } catch {
    throw new MeldbarerFehler(OHNE_NETZ);
  }
  if (!antwort.ok) {
    // Der Speicher antwortet in XML und auf Englisch — nichts davon geht nach vorn.
    throw new MeldbarerFehler("Das Bild ließ sich nicht ablegen. Bitte versuch es noch einmal.");
  }
}

async function bildFertigmelden(
  projektId: string,
  bildId: string,
  typ: string,
  breite: number,
  hoehe: number,
): Promise<void> {
  await anFrage(
    `/api/projekte/${encodeURIComponent(projektId)}/bilder/${encodeURIComponent(bildId)}/fertig`,
    { method: "POST", body: JSON.stringify({ typ, breite, hoehe }) },
  );
}

export interface HochgeladenesBild {
  bildId: string;
  zuKlein: boolean;
}

/**
 * Ein Foto vollständig hochladen.
 *
 * Wirft `MeldbarerFehler` mit einem Satz, der angezeigt werden darf — jeder andere
 * Fehler ist ein Programmfehler und gehört nicht vor die Kundschaft.
 */
export async function bildHochladen(projektId: string, datei: File): Promise<HochgeladenesBild> {
  // Die Größe zuerst, noch vor dem Dekodieren: ein 80-MB-Bild soll nicht erst eine
  // Zeichenfläche belegen, um dann abgewiesen zu werden.
  if (datei.size > MAX_ORIGINAL_BYTES) {
    throw new MeldbarerFehler("Dieses Bild ist zu groß. Bitte lade eine kleinere Fassung hoch.");
  }

  let vorschau;
  try {
    vorschau = await macheVorschau(datei);
  } catch (fehler) {
    if (fehler instanceof VorschauFehler) throw new MeldbarerFehler(fehler.message);
    throw fehler;
  }

  // Ortsdaten aus dem **Original**. Das Vorschaubild braucht es nicht: es kommt frisch
  // von einer Zeichenfläche und hat gar keine Metadaten (`src/lib/vorschau.ts`).
  const flicken = await findLocationPatches(readerFromBlob(datei), datei.type);
  const original = applyPatches(datei, flicken);

  const angemeldet = await bildAnmelden(projektId, datei.type, original.size);
  // Nacheinander und nicht nebeneinander: auf einem Telefon im Mobilfunk teilen sich
  // zwei gleichzeitige PUTs dieselbe schmale Leitung, und die Vorschau — das kleinere
  // und für die Oberfläche wichtigere Stück — käme später an.
  await inDenSpeicher(angemeldet.vorschau, vorschau.blob, "image/jpeg");
  await inDenSpeicher(angemeldet.original, original, datei.type);

  await bildFertigmelden(projektId, angemeldet.bildId, datei.type, vorschau.breite, vorschau.hoehe);
  return { bildId: angemeldet.bildId, zuKlein: vorschau.zuKlein };
}

export interface Bilderliste {
  bilder: BildAusListe[];
  /** Wann die URLs in dieser Liste ablaufen — die Grundlage fürs Nachsignieren (D5). */
  gueltigBis: number;
}

export async function bilderHolen(projektId: string): Promise<Bilderliste> {
  const rumpf = await anFrage(`/api/projekte/${encodeURIComponent(projektId)}/bilder`);
  return rumpf as Bilderliste;
}

/**
 * Den Umriss eines Bildes speichern (Schritt 3).
 *
 * Gerundet wird **vor** dem Senden, damit der Bildschirm genau das zeigt, was gleich
 * in der Zeile steht. Der Server rundet noch einmal — die Rundung ist idempotent, und
 * nur so hängt die gespeicherte Form nicht an einem Client (`shared/umriss.ts`).
 */
export async function umrissSpeichern(
  projektId: string,
  bildId: string,
  umriss: Umriss,
): Promise<void> {
  await anFrage(
    `/api/projekte/${encodeURIComponent(projektId)}/bilder/${encodeURIComponent(bildId)}/umriss`,
    { method: "PUT", body: JSON.stringify({ umriss: rundeUmriss(umriss) }) },
  );
}

/**
 * Ein Bild wieder aus dem Projekt nehmen (Annas Test am 29.09.2026).
 *
 * Ohne Rumpf und ohne Rückgabe: was danach in der Reihe steht, weiß der Aufrufer schon —
 * er hat die Liste. Ein zweiter Netzaufruf, nur um dieselbe Liste ohne eine Zeile zu
 * lesen, ließe die Reihe bei jedem Entfernen stocken (dieselbe Begründung wie bei der
 * Warteschlange in `src/App.tsx`).
 *
 * Der Server antwortet auch dann mit 200, wenn die Zeile schon weg war — zweimal
 * getippt ist kein Fehler.
 */
export async function bildEntfernen(projektId: string, bildId: string): Promise<void> {
  await anFrage(
    `/api/projekte/${encodeURIComponent(projektId)}/bilder/${encodeURIComponent(bildId)}`,
    { method: "DELETE" },
  );
}

/**
 * Die ganze Reihe speichern (Schritt 4).
 *
 * **Immer die ganze Reihe, nie eine einzelne Figur.** Eine Figur von Platz 55 auf 3 zu
 * setzen ändert 52 Plätze; einzeln geschickt wären das 52 Anfragen, von denen die Hälfte
 * nach dem nächsten Handgriff ankäme. Und ein abgewiesener Stand bleibt so nicht liegen:
 * die nächste Änderung schickt alles erneut.
 *
 * `amEnde` setzt `keepalive`. Ohne das bricht der Browser die Anfrage mit der Seite ab,
 * und der letzte Handgriff wäre weg — es ist der Fall „Tab zu, Telefon gesperrt" aus dem
 * Abnahmekriterium von Schritt 6. **Kein `navigator.sendBeacon`:** der Server verlangt
 * einen `Origin`-Kopf (`server/http.ts`, `isAllowedOrigin`), und Beacon schickt ihn nicht
 * verlässlich mit.
 */
export async function anordnungSpeichern(
  projektId: string,
  elemente: readonly Anordnung[],
  amEnde = false,
): Promise<void> {
  await anFrage(`/api/projekte/${encodeURIComponent(projektId)}/anordnung`, {
    method: "PUT",
    body: JSON.stringify({ elemente }),
    keepalive: amEnde,
  });
}

/**
 * Wen ein Bildelement zeigt (Schritt 5).
 *
 * Beide Angaben in einer Anfrage, weil sie denselben Bildschirm haben — zwei Aufrufe
 * hießen zwei Wege, auf denen die Hälfte ankommt (`server/app.ts`).
 *
 * Der Server gibt die **gereinigte** Beschriftung zurück, und der Aufrufer übernimmt
 * genau die: sonst stünde auf dem Bildschirm etwas anderes als in der Zeile, und beim
 * nächsten Öffnen wechselte der Text ohne Zutun.
 */
export async function personenSpeichern(
  projektId: string,
  bildId: string,
  anzahl: number,
  beschriftung: string,
): Promise<string> {
  const rumpf = await anFrage(
    `/api/projekte/${encodeURIComponent(projektId)}/bilder/${encodeURIComponent(bildId)}/personen`,
    { method: "PUT", body: JSON.stringify({ anzahl, beschriftung }) },
  );
  const { beschriftung: zurueck } = rumpf as { beschriftung?: unknown };
  return typeof zurueck === "string" ? zurueck : putzeBeschriftung(beschriftung);
}
